/**
 * AppsMarketplaceSection — the App Marketplace rendered as a section
 * inside MarketplacePage. No outer `<div className="marketplace-page">`
 * wrapper; the parent owns that. Use this when you want the apps tiles
 * to sit alongside / below the workflow tiles on the same page.
 *
 * For the standalone deep-link `/marketplace/apps`, AppsMarketplacePage
 * wraps this same component with its own page wrapper.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { IonIcon, IonSpinner } from '@ionic/react';
import { rocketOutline, openOutline, alertCircle, star, starOutline, addOutline } from 'ionicons/icons';

import { Apps } from '../../services/apps';
import ProjectsService from '../../services/projects';
import authService from '../../services/auth';
import Credits, { CREDIT_CHANGED_EVENT, formatBalance, hoursOfRuntime } from '../../services/credits';
import WorkspaceCredentials from '../../services/workspaceCredentials';
import ClaudeCredentialModal from '../../components/integrations/ClaudeCredentialModal';
import OpenAICredentialModal from '../../components/integrations/OpenAICredentialModal';
import Field from '../../components/atoms/Field/Field';
import Dropdown from '../../components/atoms/Dropdown/Dropdown';
import AppPricingBadge, { resolveTier } from '../../components/billing/AppPricingBadge';
import { projectAppsMarketplacePath } from '../../utils/marketplacePaths';
import MktAlertModal, { MktAlertModalLink } from '../../components/molecules/MktAlertModal/MktAlertModal';
// Share the same Choose-a-Project + Create-new styles the workflow
// deploy wizard uses — so apps deploy flow looks identical.
import '../MarketplacePage/MarketplaceDeployWizardModal.css';
import '../MarketplacePage/MarketplaceDetailModal.css';
import './AppsMarketplacePage.css';

/**
 * @param {Object} props
 * @param {boolean} [props.showHeader=true]  Render the section's own h2 +
 *   subtitle. Pass false when the parent page already provides the
 *   heading (e.g. the standalone /marketplace/apps wrapper).
 * @param {boolean} [props.embedded=false]  When true (combined /marketplace
 *   page below workflows), show the top divider + extra spacing. Standalone
 *   /marketplace/apps and project-scoped routes leave this false.
 */
// Special-case labels for short / acronym categories so chips read
// nicely. Anything not in the map is title-cased.
const APPS_CATEGORY_LABELS = {
  ai: 'AI',
  ci: 'CI',
  db: 'DB',
};
function formatCategoryLabel(name) {
  if (!name) return '—';
  const lower = String(name).toLowerCase();
  if (APPS_CATEGORY_LABELS[lower]) return APPS_CATEGORY_LABELS[lower];
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

const AppsMarketplaceSection = ({ showHeader = true, embedded = false }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Active category chip (null === "All"). Same model as the workflow
  // marketplace chip row above — single-select toggle.
  const [activeFacet, setActiveFacet] = useState(null);

  // Deploy modal state
  const [active, setActive] = useState(null);     // catalog tile (list-shape)
  const [activeDetail, setActiveDetail] = useState(null); // full detail (with myRating)
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [pickedProject, setPickedProject] = useState('');
  const [instanceName, setInstanceName] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [deployError, setDeployError] = useState(null);

  // Rating UX state (mirrors workflow modal)
  const [hoverRating, setHoverRating] = useState(0);
  const [submittingRating, setSubmittingRating] = useState(false);

  // Two-step deploy flow (mirrors the workflow detail → wizard
  // hand-off). Step 1 = info / ratings / cost / facts; step 2 = pick
  // a project + name the instance + Deploy. Project-scoped opens
  // (e.g. /projects/:id/marketplace) skip step 1's project picker but
  // still preserve the same visual two-stage flow.
  const [step, setStep] = useState(1);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);

  // Credit balance for the cost banner in the deploy modal. null = not
  // yet fetched. Reloaded on every modal-open + when the global
  // `credit-changed` event fires (post-checkout return).
  const [creditBalanceMc, setCreditBalanceMc] = useState(null);
  // Drives the "Add credits to deploy" alert modal — opened when a
  // deploy is attempted without enough balance (front-end fast path or
  // backend rejection). See handleDeploy.
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  // Drives the small "Confirm deploy" modal. Opens after the user clicks
  // Deploy and we've verified a credential exists for the picked provider.
  // The actual POST runs only after the user confirms in this modal.
  const [showConfirmDeploy, setShowConfirmDeploy] = useState(false);
  // When the user clicked Deploy but had no credential for the picked
  // provider, we open the credential paste modal AND remember to re-open
  // the confirm modal once they finish pasting. Cleared once the chain
  // either completes or is cancelled.
  const [pendingConfirmAfterCred, setPendingConfirmAfterCred] = useState(false);
  // Lowercased set of instance names already used in the target project
  // — drives real name-collision detection (the backend does NOT enforce
  // name uniqueness, so two same-named instances are otherwise allowed).
  const [existingNames, setExistingNames] = useState(() => new Set());

  // Agent provider — 'claude' (default) or 'codex'. Drives which BYOK
  // credential gate the deploy modal checks, and what the POST body
  // sends. State is reset to 'claude' on each modal open so picking
  // codex for one app doesn't sticky across the next deploy.
  const [pickedProvider, setPickedProvider] = useState('claude');

  // CPU architecture for the deploy — 'x86_64' or 'arm64'. Defaults to
  // the FIRST entry in the catalog's `architectures` array (operator
  // preference; pre-rollout entries are ['x86_64']). Picker only renders
  // when the catalog supports BOTH; single-arch catalog entries pin
  // silently. Reset on every modal open so a prior pick doesn't sticky.
  const [pickedArchitecture, setPickedArchitecture] = useState('x86_64');

  // BYOK credential gate per provider. State drives the two deploy-modal
  // states (per provider):
  //   { has<X>: true,  mode, suffix } → State A (one-click deploy with a
  //                                       "using …abc123" affordance)
  //   { has<X>: false }              → State B (paste-token modal opens
  //                                       inline; Save → re-fetch → State A)
  // null means "not yet fetched" — we lazy-fetch on modal open + when the
  // backend 428s inline (race-safety: cred deleted between open + Deploy).
  const [claudeCred, setClaudeCred] = useState(null);
  const [openaiCred, setOpenaiCred] = useState(null);
  const [claudeModalOpen, setClaudeModalOpen] = useState(false);
  const [openaiModalOpen, setOpenaiModalOpen] = useState(false);
  const refreshClaudeCred = useCallback(async () => {
    const status = await WorkspaceCredentials.getClaudeStatus();
    setClaudeCred(status || { hasClaude: false });
    return status;
  }, []);
  const refreshOpenaiCred = useCallback(async () => {
    const status = await WorkspaceCredentials.getOpenAIStatus();
    setOpenaiCred(status || { hasOpenAI: false });
    return status;
  }, []);

  const { projectId: projectScopedId } = useParams();
  const navigate = useNavigate();
  const isAuthed = authService.isAuthenticated?.() ?? false;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await Apps.listCatalog();
        if (cancelled) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load apps catalog:', err);
        setError(err.message || 'Failed to load catalog');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // When the URL is project-scoped (/projects/:id/marketplace) we skip
  // the project-picker entirely — the user already chose. We still
  // fetch the project NAME so the modal can show which project the
  // instance lands in. Outside a project, we fetch the full list so
  // the user can pick.
  const ensureProjects = useCallback(async () => {
    if (projectScopedId) {
      // Already inside a project — pin to that id and resolve its name
      // for display. A single-project fetch would be cheaper but the
      // shared service only exposes a list call; the result is cached.
      if (pickedProject === projectScopedId && projects.length > 0) return;
      setProjectsLoading(true);
      try {
        const res = await ProjectsService.getProjects();
        const list = res?.projects || [];
        setProjects(list);
        setPickedProject(projectScopedId);
      } catch (err) {
        console.error('Failed to resolve project name:', err);
        setPickedProject(projectScopedId);
      } finally {
        setProjectsLoading(false);
      }
      return;
    }
    if (projects.length > 0 || projectsLoading) return;
    setProjectsLoading(true);
    try {
      const res = await ProjectsService.getProjects();
      const list = res?.projects || [];
      setProjects(list);
      setPickedProject(list[0]?.projectId || list[0]?.id || '');
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setProjectsLoading(false);
    }
  }, [projects.length, projectsLoading, projectScopedId, pickedProject]);

  // Project name lookup for the "deploying to" line shown when the
  // picker is hidden (project-scoped route).
  const pickedProjectName = React.useMemo(() => {
    const p = projects.find((x) => (x.projectId || x.id) === pickedProject);
    return p?.name || '';
  }, [projects, pickedProject]);

  const openDeploy = useCallback(async (entry) => {
    if (!isAuthed) {
      const next = projectScopedId
        ? projectAppsMarketplacePath(projectScopedId)
        : '/marketplace/apps';
      navigate(`/signup?next=${encodeURIComponent(next)}`);
      return;
    }
    setActive(entry);
    setActiveDetail(entry); // optimistic — show list fields immediately
    setInstanceName('');
    setDeployError(null);
    setHoverRating(0);
    setStep(1);
    setShowCreateForm(false);
    setNewProjectName('');
    // Project-scoped: pin the picked project immediately so the inline
    // instance-name + Deploy footer renders right away (no flash while
    // ensureProjects resolves the name async). The detail body + this
    // footer then show together in one modal — no Continue step.
    if (projectScopedId) setPickedProject(projectScopedId);
    ensureProjects();
    // Fetch credit balance for the cost banner. Non-fatal — if the API
    // is unreachable the banner just shows "balance unknown" without
    // blocking deploy.
    Credits.getBalance()
      .then((b) => setCreditBalanceMc(Number(b?.balanceMilliCents) || 0))
      .catch(() => setCreditBalanceMc(null));
    // Reset provider pick to Claude on every modal open — sticky picks
    // across apps would be surprising. The user re-selects explicitly
    // per deploy.
    setPickedProvider('claude');
    // Default architecture = catalog's first entry. For dual-arch tiles
    // operator chooses preference order in the catalog JSON (n8n lists
    // arm64 first to nudge users into the cheaper Fargate compute). For
    // single-arch tiles this still works — the picker just isn't shown.
    {
      const arches = Array.isArray(entry.architectures) && entry.architectures.length > 0
        ? entry.architectures : ['x86_64'];
      setPickedArchitecture(arches[0]);
    }
    // Fetch BOTH provider credential statuses (BYOK gates). State A vs
    // State B branch per provider is driven by these; refresh on modal
    // open so a token added in another tab is reflected without a page
    // reload.
    refreshClaudeCred();
    refreshOpenaiCred();
    // Refetch detail so we have myRating + fresh aggregates the moment
    // the modal opens. Non-fatal if it fails — list fields are enough.
    try {
      const fresh = await Apps.getCatalogEntry(entry.appType);
      setActiveDetail(fresh);
    } catch (e) {
      console.warn('Failed to refresh catalog detail:', e.message);
    }
  }, [isAuthed, navigate, ensureProjects, refreshClaudeCred, refreshOpenaiCred]);

  // Listen for global credit-changed events so the banner stays fresh
  // if the user tops up in another tab while the modal is open.
  useEffect(() => {
    const handler = () => {
      if (!active) return;
      Credits.getBalance()
        .then((b) => setCreditBalanceMc(Number(b?.balanceMilliCents) || 0))
        .catch(() => {});
    };
    window.addEventListener(CREDIT_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CREDIT_CHANGED_EVENT, handler);
  }, [active]);

  // Fetch the names already taken in the target project so the deploy
  // form can flag a collision before submit. Re-runs when the picked
  // project changes (project-scoped pins it immediately; global picks
  // it on step 2). Destroyed instances free their name for reuse.
  useEffect(() => {
    if (!active || !pickedProject) { setExistingNames(new Set()); return undefined; }
    let cancelled = false;
    Apps.listInstances({ projectId: pickedProject })
      .then((res) => {
        if (cancelled) return;
        // listApps returns `{ apps: [...] }` (NOT `items`); the server
        // already excludes destroyed rows, so everything returned counts
        // as a taken name.
        const list = res?.apps || res?.items || [];
        const names = new Set(
          list
            .map((it) => String(it.displayName || it.name || '').trim().toLowerCase())
            .filter(Boolean),
        );
        setExistingNames(names);
      })
      .catch(() => { if (!cancelled) setExistingNames(new Set()); });
    return () => { cancelled = true; };
  }, [active, pickedProject]);

  const closeDeploy = () => {
    if (deploying) return;
    setActive(null);
    setActiveDetail(null);
    setDeployError(null);
    setHoverRating(0);
    setStep(1);
    setShowCreateForm(false);
    setNewProjectName('');
  };

  // 0-project users: when they reach step 2, skip the dropdown and land
  // in the create form so they can name their first project immediately.
  useEffect(() => {
    if (!active || step !== 2 || projectScopedId || projectsLoading) return;
    if (projects.length === 0 && !pickedProject && !showCreateForm) {
      setShowCreateForm(true);
    }
  }, [active, step, projectScopedId, projectsLoading, projects.length, pickedProject, showCreateForm]);

  // Create + auto-pick a new project. Mirrors MarketplaceDeployWizardModal's
  // handleCreateAndPick.
  const handleCreateAndPick = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim() || creatingProject) return;
    setCreatingProject(true);
    try {
      const created = await ProjectsService.createProject({ name: newProjectName.trim() });
      // The API may return { project: {...} } or the row directly.
      const row = created?.project || created;
      if (row?.id || row?.projectId) {
        const id = row.id || row.projectId;
        setProjects((list) => [...list, row]);
        setPickedProject(id);
        setShowCreateForm(false);
        setNewProjectName('');
      }
    } catch (err) {
      console.error('Create project failed:', err);
      setDeployError(err.message || 'Could not create project');
    } finally {
      setCreatingProject(false);
    }
  };

  // Submit a rating. Backend recomputes the META aggregate atomically;
  // the response gives us the new avg + count without a follow-up GET.
  // Also patch the corresponding tile in `items` so the list view's
  // rating chip updates immediately without re-fetching the catalog.
  const submitRating = useCallback(async (n) => {
    if (!active?.appType || submittingRating) return;
    setSubmittingRating(true);
    try {
      const res = await Apps.rate(active.appType, { rating: n });
      const next = {
        ratingAvg: Number(res.ratingAvg) || 0,
        ratingCount: Number(res.ratingCount) || 0,
        ratingHistogram: res.ratingHistogram || activeDetail?.ratingHistogram,
      };
      setActiveDetail((prev) => prev ? {
        ...prev,
        ...next,
        myRating: { rating: n, review: '', ratedAt: new Date().toISOString() },
      } : prev);
      setItems((list) => list.map((it) => (
        it.appType === active.appType ? { ...it, ...next } : it
      )));
    } catch (e) {
      console.error('Rate failed:', e);
    } finally {
      setSubmittingRating(false);
    }
  }, [active, activeDetail, submittingRating]);

  // Click handler on the row's Deploy button. Doesn't fire the POST
  // directly anymore — instead it runs the local gates (name collision,
  // credit balance, BYOK credential) and either:
  //   - opens the credential paste modal (no cred → user must paste),
  //   - opens the confirm-deploy modal (cred present → user confirms),
  // OR opens the insufficient-credits modal. The actual POST runs from
  // `performDeploy` after the confirm modal's primary button fires.
  const handleDeployClick = () => {
    if (!active || !pickedProject) return;
    // Block duplicate names in the same project (backend doesn't enforce).
    const wanted = (instanceName.trim() || active.displayName || '').trim().toLowerCase();
    if (wanted && existingNames.has(wanted)) return;
    // Fast path — if we already know the balance is short, pop the
    // top-up modal without a round-trip. When the balance hasn't
    // loaded yet (creditBalanceMc null) we skip this and let the
    // backend be the gate; the catch in performDeploy routes its
    // insufficient-credits rejection to the SAME modal.
    const tierInfo = resolveTier(active.pricing?.tier);
    const fiveMinCost = tierInfo.milliCentsPerMin * 5;
    if (creditBalanceMc !== null && creditBalanceMc < fiveMinCost) {
      setShowInsufficientCredits(true);
      return;
    }
    // BYOK credential gate. If we haven't fetched yet (cred === null)
    // we skip and proceed to confirm — the backend will 428 if needed
    // and performDeploy catches that and pops the paste modal.
    const isCodex = pickedProvider === 'codex';
    const cred = isCodex ? openaiCred : claudeCred;
    const hasCred = isCodex ? cred?.hasOpenAI : cred?.hasClaude;
    if (cred && hasCred === false) {
      // No cred — open the paste modal directly. After save, the
      // onConnected callback re-opens the confirm modal.
      setPendingConfirmAfterCred(true);
      if (isCodex) setOpenaiModalOpen(true);
      else setClaudeModalOpen(true);
      return;
    }
    // Cred present (or unknown — proceed and let backend gate) →
    // open the confirm modal. The real POST fires on confirm.
    setShowConfirmDeploy(true);
  };

  const performDeploy = async () => {
    if (!active || !pickedProject) return;
    setShowConfirmDeploy(false);
    setDeploying(true);
    setDeployError(null);
    try {
      // Forward architecture ONLY when the user picked one explicitly
      // AND the catalog supports multiple. Single-arch tiles let the
      // backend default kick in (catalog's first arch) so the wire shape
      // stays byte-identical for the pre-rollout consumers.
      const activeArches = Array.isArray(active.architectures) && active.architectures.length > 0
        ? active.architectures : ['x86_64'];
      const archMatters = activeArches.length > 1;
      const res = await Apps.deploy({
        appType: active.appType,
        projectId: pickedProject,
        name: instanceName || undefined,
        // Only forward when picked is non-default — keeps the wire shape
        // unchanged for Claude deploys (matches every CLI/MCP request).
        provider: pickedProvider !== 'claude' ? pickedProvider : undefined,
        architecture: archMatters ? pickedArchitecture : undefined,
      });
      const instanceId = res?.instanceId;
      if (instanceId) {
        navigate(`/projects/${pickedProject}/apps/${instanceId}`);
      } else {
        closeDeploy();
      }
    } catch (err) {
      console.error('Deploy failed:', err);
      const msg = err.message || 'Deploy failed';
      const code = err?.body?.code || err?.code;
      // Insufficient-credits rejection → the dedicated "Add credits to
      // deploy" modal, NOT an inline red banner. Match the backend's
      // wording ("Insufficient credits … Need at least … runway …").
      if (/insufficient credit|need at least|top up|runway/i.test(msg)) {
        setShowInsufficientCredits(true);
      } else if (code === 'CLAUDE_CREDENTIAL_REQUIRED') {
        // Race: cred existed at modal-open, deleted before Deploy click.
        // Flip into State B by clearing the cached status + opening the
        // paste-token modal. Same code-only contract the CLI uses; never
        // parse the human message.
        setClaudeCred({ hasClaude: false });
        setClaudeModalOpen(true);
      } else if (code === 'OPENAI_CREDENTIAL_REQUIRED') {
        // Codex counterpart of the Claude race above. Provider was
        // picked=codex but the OpenAI cred vanished between open and
        // Deploy (or wasn't yet present).
        setOpenaiCred({ hasOpenAI: false });
        setOpenaiModalOpen(true);
      } else {
        setDeployError(msg);
      }
      setDeploying(false);
    }
  };

  // Render nothing (not even the section heading) while loading or
  // when the catalog endpoint is unreachable. Local dev that hasn't
  // restarted devup.sh after pulling the catalog code will 404 on
  // GET /apps/catalog — better to hide the whole section than show a
  // scary "Not Found" panel beneath the workflow marketplace.
  if (loading || error || items.length === 0) return null;

  // Build the chip row from the union of catalog categories. Sort by
  // count desc → ties by label asc, so the most-represented category
  // sits next to the "All" pill.
  const facets = (() => {
    const counts = new Map();
    for (const it of items) {
      const c = String(it.category || '').trim().toLowerCase();
      if (!c) continue;
      counts.set(c, (counts.get(c) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count, label: formatCategoryLabel(name) }))
      .sort((a, b) => (b.count - a.count) || a.label.localeCompare(b.label));
  })();

  // Visible items: apply category filter. Null facet === show all.
  const visibleItems = activeFacet
    ? items.filter((it) => String(it.category || '').toLowerCase() === activeFacet)
    : items;

  // Effective instance name = what the user typed, else the default the
  // backend would use (catalog displayName). Collision = that name (case-
  // insensitive) already exists in the project. `active?.` keeps this
  // safe to evaluate even when no modal is open.
  const effectiveName = (instanceName.trim() || active?.displayName || '').trim().toLowerCase();
  const nameTaken = !!effectiveName && existingNames.has(effectiveName);

  return (
    <>
      <section
        className={`marketplace-section apps-section${embedded ? ' apps-section--embedded' : ''}`}
      >
        {showHeader && (
          <header className="apps-section-header">
            <h2 className="apps-section-title">App Marketplace</h2>
            <p className="apps-section-subtitle">
              One-click hosted instances of popular open-source apps. Each
              instance is private to you, with an AI ops agent for install,
              monitoring, and upgrades.
            </p>
          </header>
        )}

        {/* Category chip row — mirrors the Workflow Marketplace chips
            (same `marketplace-chips*` classes so the styling rides
            along). Hidden when there's only one category — single-axis
            filter would be a no-op. */}
        {facets.length > 1 && (
          <div className="marketplace-chips-wrap">
            <nav className="marketplace-chips" aria-label="Filter apps">
              <button
                type="button"
                className={`marketplace-chip${activeFacet === null ? ' is-active' : ''}`}
                onClick={() => setActiveFacet(null)}
                aria-pressed={activeFacet === null}
              >
                All
                <span className="marketplace-chip-count">{items.length}</span>
              </button>
              {facets.map((facet) => {
                const isActive = activeFacet === facet.name;
                return (
                  <button
                    key={facet.name}
                    type="button"
                    className={`marketplace-chip${isActive ? ' is-active' : ''}`}
                    onClick={() => setActiveFacet(isActive ? null : facet.name)}
                    aria-pressed={isActive}
                  >
                    {facet.label}
                    <span className="marketplace-chip-count">{facet.count}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        )}

        <ol className="marketplace-grid">
          {visibleItems.map((entry, idx) => (
            <li key={entry.appType} className="marketplace-card-li">
              <button
                type="button"
                className="marketplace-card"
                onClick={() => openDeploy(entry)}
                aria-label={`Deploy ${entry.displayName}`}
              >
                <span className="marketplace-rank">{idx + 1}</span>
                <span className="marketplace-card-icon-wrap">
                  {entry.icon ? (
                    // eslint-disable-next-line jsx-a11y/img-redundant-alt
                    <img
                      src={entry.icon}
                      alt=""
                      className="marketplace-card-icon"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="marketplace-card-icon marketplace-card-icon--fallback">
                      {(entry.displayName || entry.appType).slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </span>
                <span className="marketplace-card-body">
                  <span className="marketplace-card-name">
                    {entry.displayName}
                    <span className="apps-card-version">v{entry.appVersion}</span>
                  </span>
                  <span className="marketplace-card-tagline">{entry.tagline}</span>
                  <span className="marketplace-card-author">
                    By Zibby
                    <span className="marketplace-card-author-badge">Official</span>
                    {Number(entry.ratingCount) > 0 && (
                      <span
                        className="marketplace-card-rating"
                        aria-label={`${Number(entry.ratingAvg).toFixed(1)} stars, ${entry.ratingCount} rating${entry.ratingCount === 1 ? '' : 's'}`}
                      >
                        <IonIcon icon={star} aria-hidden="true" />
                        <span className="marketplace-card-rating-value">{Number(entry.ratingAvg).toFixed(1)}</span>
                        <span className="marketplace-card-rating-count">({entry.ratingCount})</span>
                      </span>
                    )}
                    {/* Pricing tier pill — fed by `pricing.tier` from the
                        catalog row (Agent C). Falls back to 'standard'
                        when missing so legacy catalog entries still
                        render a badge instead of nothing. */}
                    <AppPricingBadge tier={entry.pricing?.tier} className="apps-card-pricing" />
                  </span>
                </span>
                {/* Architecture pills — bottom-right corner. Always
                    render both x86 + arm; the unsupported one is
                    dimmed. Operator preference (which arch the
                    catalog lists FIRST) doesn't change rendering
                    here; that's a deploy-time concern. */}
                {(() => {
                  const arches = Array.isArray(entry.architectures) && entry.architectures.length > 0
                    ? entry.architectures : ['x86_64'];
                  const supportsX86 = arches.includes('x86_64');
                  const supportsArm = arches.includes('arm64');
                  const hasBoth = supportsX86 && supportsArm;
                  return (
                    <span
                      className="apps-card-arches"
                      aria-label={`Architectures: ${arches.join(', ')}`}
                      title={hasBoth ? 'ARM is ~20% greener (same price)' : `Runs on ${arches.join(' / ')}`}
                    >
                      <span
                        className={`apps-card-arch apps-card-arch--x86 ${supportsX86 ? 'apps-card-arch--on' : 'apps-card-arch--off'}`}
                        aria-hidden="true"
                      >x86</span>
                      <span
                        className={`apps-card-arch apps-card-arch--arm ${supportsArm ? 'apps-card-arch--on' : 'apps-card-arch--off'}`}
                        aria-hidden="true"
                      >arm</span>
                    </span>
                  );
                })()}
              </button>
            </li>
          ))}
        </ol>
      </section>

      {active && createPortal(
        <div className="mkt-modal-overlay" onClick={closeDeploy} role="presentation">
          <div
            className="mkt-modal"
            role="dialog"
            aria-label={`Deploy ${active.displayName}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="mkt-modal-close"
              onClick={closeDeploy}
              aria-label="Close"
              disabled={deploying}
            >×</button>

            <div className="mkt-modal-body">
              {/* Reuse the workflow-detail-modal header shape (icon
                  centered on top, title centered below) so the apps
                  modal looks like a sibling of the workflow modal. */}
              <header className="mkt-modal-header">
                {active.icon ? (
                  // eslint-disable-next-line jsx-a11y/img-redundant-alt
                  <img src={active.icon} alt="" className="mkt-modal-icon" />
                ) : (
                  <div className="mkt-modal-icon mkt-modal-icon--fallback">
                    {(active.displayName || active.appType).slice(0, 1).toUpperCase()}
                  </div>
                )}
                <h2 className="mkt-modal-title">
                  {(step === 2 && !projectScopedId) ? `Deploy ${active.displayName}` : active.displayName}
                </h2>
                {(step === 1 || projectScopedId) && (
                  <p className="mkt-modal-author">
                    By Zibby
                    <span className="mkt-modal-author-badge">Official</span>
                  </p>
                )}
                <p className="mkt-modal-tagline">
                  {(step === 2 && !projectScopedId)
                    ? "Apps live inside projects. Pick an existing project — or create a new one — and we'll deploy this instance into it."
                    : active.tagline}
                </p>

                {/* Stats row — only on step 1 (detail). Step 2 (wizard)
                    is minimal: just icon + title + tagline + choose-a-
                    project, matching the workflow deploy wizard. */}
                {(step === 1 || projectScopedId) && (() => {
                  const detail = activeDetail || active;
                  const rating = Number(detail.ratingAvg) || 0;
                  const myRatingValue = Number(detail.myRating?.rating) || 0;
                  const canRate = isAuthed;
                  const starsFilled = hoverRating || myRatingValue || Math.round(rating);
                  return (
                    <div className="mkt-modal-stats" aria-label="Stats">
                      <div className="mkt-modal-stat">
                        <div className="mkt-modal-stat-value">
                          <span className="mkt-modal-stat-number">{rating.toFixed(1)}</span>
                          <span
                            className={`mkt-modal-stat-stars ${canRate ? 'is-interactive' : ''}`}
                            role={canRate ? 'radiogroup' : undefined}
                            aria-label={canRate ? 'Rate this app' : undefined}
                            onMouseLeave={canRate ? () => setHoverRating(0) : undefined}
                          >
                            {[1, 2, 3, 4, 5].map((n) => {
                              const filled = n <= starsFilled;
                              const StarEl = canRate ? 'button' : 'span';
                              const extra = canRate ? {
                                type: 'button',
                                role: 'radio',
                                'aria-checked': n === myRatingValue,
                                'aria-label': `Rate ${n} star${n === 1 ? '' : 's'}`,
                                disabled: submittingRating,
                                onMouseEnter: () => setHoverRating(n),
                                onFocus: () => setHoverRating(n),
                                onBlur: () => setHoverRating(0),
                                onClick: () => submitRating(n),
                              } : {};
                              return (
                                <StarEl key={n} className="mkt-modal-star" {...extra}>
                                  <IonIcon icon={filled ? star : starOutline} aria-hidden="true" />
                                </StarEl>
                              );
                            })}
                          </span>
                        </div>
                        <div className="mkt-modal-stat-label">
                          {(Number(detail.ratingCount) || 0)} RATING{Number(detail.ratingCount) === 1 ? '' : 'S'}
                        </div>
                      </div>
                      <div className="mkt-modal-stat">
                        <div className="mkt-modal-stat-value mkt-modal-stat-category">
                          {active.category || '—'}
                        </div>
                        <div className="mkt-modal-stat-label">Category</div>
                      </div>
                      <div className="mkt-modal-stat">
                        <div className="mkt-modal-stat-value">v{active.appVersion}</div>
                        <div className="mkt-modal-stat-label">Version</div>
                      </div>
                    </div>
                  );
                })()}
              </header>

              {/* ── STEP 1 — detail/info view. Cost banner + heads-up
                  + facts. Footer is a single "Continue" CTA that flips
                  to step 2 (the actual deploy wizard). */}
              {(step === 1 || projectScopedId) && (() => {
                const tierInfo = resolveTier(active.pricing?.tier);
                const ratePerMin = tierInfo.milliCentsPerMin;
                const balance = creditBalanceMc;
                const hrs = balance !== null ? hoursOfRuntime(ratePerMin, balance) : null;
                const balanceStr = balance !== null ? formatBalance(balance) : '—';
                const hrsStr = hrs !== null
                  ? (hrs >= 1
                      ? `≈ ${Math.floor(hrs).toLocaleString()} hours of runtime`
                      : (hrs > 0 ? `≈ ${Math.round(hrs * 60)} min of runtime` : 'insufficient runtime'))
                  : '';
                return (
                  <>
                    <div className="apps-modal-cost-banner">
                      <strong>This app costs {tierInfo.hourly} while running.</strong>
                      {' '}Your balance: <strong>{balanceStr}</strong>
                      {hrsStr ? ` (${hrsStr})` : ''}
                    </div>

                    {active.ui?.setupHint && (
                      <p className="apps-modal-hint">
                        <strong>Heads up:</strong> {active.ui.setupHint}
                      </p>
                    )}

                    <ul className="apps-modal-facts">
                      <li><strong>Public URL</strong> <span>https://<em>{'<id>'}</em>.apps.zibby.dev</span></li>
                      <li><strong>Resources</strong> <span>{(active.resources.cpu / 1024).toFixed(2)} vCPU · {(active.resources.memory / 1024).toFixed(2)} GB RAM</span></li>
                      {active.homepage && (
                        <li>
                          <strong>Homepage</strong>
                          <span>
                            <a
                              href={active.homepage}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="apps-modal-homepage-link"
                            >
                              <IonIcon icon={openOutline} aria-hidden="true" />
                              {active.homepage.replace(/^https?:\/\//, '')}
                            </a>
                          </span>
                        </li>
                      )}
                    </ul>
                  </>
                );
              })()}

              {/* ── STEP 2 — choose a project. Same shape + classes as
                  the workflow MarketplaceDeployWizardModal. Hidden
                  when the URL is already project-scoped (we know which
                  project this app gets deployed into). */}
              {step === 2 && !projectScopedId && (
                <section className="mkt-modal-section">
                  <h3 className="mkt-modal-section-title">Choose a project</h3>

                  {projectsLoading && projects.length === 0 ? (
                    <div className="mkt-wizard-loading">
                      <IonSpinner name="crescent" />
                      <span>Loading your projects…</span>
                    </div>
                  ) : null}

                  {projects.length > 0 && !showCreateForm && (
                    <Dropdown
                      className="mkt-wizard-project-dropdown"
                      options={[
                        { key: '', label: 'Select a project…' },
                        ...projects.map((p) => ({ key: p.projectId || p.id, label: p.name })),
                      ]}
                      value={pickedProject || ''}
                      onChange={(v) => setPickedProject(v || '')}
                    />
                  )}

                  {!showCreateForm ? (
                    <button
                      type="button"
                      className="mkt-wizard-create-toggle"
                      onClick={() => {
                        setShowCreateForm(true);
                        setPickedProject('');
                      }}
                    >
                      <IonIcon icon={addOutline} aria-hidden="true" />
                      <span>Create a new project</span>
                    </button>
                  ) : (
                    <form onSubmit={handleCreateAndPick} className="mkt-wizard-create-form">
                      <label className="mkt-wizard-create-label" htmlFor="apps-wizard-new-project">
                        New project name
                      </label>
                      <div className="mkt-wizard-create-row">
                        <input
                          id="apps-wizard-new-project"
                          type="text"
                          className="mkt-wizard-create-input"
                          value={newProjectName}
                          onChange={(e) => setNewProjectName(e.target.value)}
                          placeholder="my-project"
                          autoFocus
                          disabled={creatingProject}
                          autoComplete="off"
                        />
                        <button
                          type="submit"
                          className="mkt-wizard-create-submit"
                          disabled={creatingProject || !newProjectName.trim()}
                        >
                          {creatingProject ? 'Creating…' : 'Create'}
                        </button>
                        {projects.length > 0 && (
                          <button
                            type="button"
                            className="mkt-wizard-create-cancel"
                            onClick={() => {
                              setShowCreateForm(false);
                              setNewProjectName('');
                            }}
                            disabled={creatingProject}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </form>
                  )}
                </section>
              )}

              {deployError && (
                <div className="apps-modal-error">
                  <IonIcon icon={alertCircle} aria-hidden="true" /> {deployError}
                </div>
              )}
            </div>

            {/* ── Footer transitions with `step`. Step 1 = single
                "Continue" CTA. Step 2 = instance-name field +
                Deploy button + Back link. Same `mkt-modal-name-*`
                classes as the workflow wizard footer. */}
            <footer className="mkt-modal-footer apps-modal-footer">
              {step === 1 && !projectScopedId && (
                <button
                  type="button"
                  className="mkt-modal-deploy mkt-modal-deploy--full"
                  onClick={() => setStep(2)}
                >
                  Continue
                </button>
              )}

              {step === 2 && !pickedProject && (
                /* No project picked yet → full-width disabled Deploy
                   placeholder. Identical shape to MarketplaceDeployWizardModal's
                   pre-pick footer. */
                <button
                  type="button"
                  className="mkt-modal-deploy mkt-modal-deploy--full"
                  disabled
                  aria-disabled="true"
                  title="Pick or create a project first"
                >
                  Deploy
                </button>
              )}

              {(step === 2 || projectScopedId) && pickedProject && (
                /* Project picked → reveal the provider picker + instance-
                   name field + final Deploy button. Same shape as the
                   workflow wizard once it transitions into the v3 footer.
                   Project-scoped shows this inline beneath the detail
                   (no Continue step) — one modal, like the workflow
                   deploy modal. */
                <>
                  {/* Architecture picker — only when the catalog tile
                      supports BOTH arches. Sits ABOVE the provider
                      picker so it's the first thing the user sees in
                      the footer wizard (cost-relevant choice → comes
                      first). Single-arch tiles skip this entirely and
                      the deploy uses that arch silently. */}
                  {(() => {
                    const arches = Array.isArray(active.architectures) && active.architectures.length > 0
                      ? active.architectures : ['x86_64'];
                    if (arches.length < 2) return null;
                    // "Recommended" tag goes on arm64 when both are
                    // supported — flags the cheaper / greener choice
                    // without changing pricing (which stays the same
                    // per the rollout's hard constraint).
                    return (
                      <div
                        role="radiogroup"
                        aria-label="CPU architecture"
                        className="apps-modal-arch-picker"
                      >
                        {arches.map((arch) => {
                          const isActive = pickedArchitecture === arch;
                          const isRecommended = arch === 'arm64';
                          const label = arch === 'arm64' ? 'ARM64 (Graviton)' : 'x86_64 (Intel/AMD)';
                          const sub = arch === 'arm64'
                            ? 'Greener · same price'
                            : 'Widest compatibility';
                          return (
                            <label
                              key={arch}
                              className={`apps-modal-arch-option${isActive ? ' is-active' : ''}`}
                              style={{ cursor: deploying ? 'not-allowed' : 'pointer' }}
                            >
                              <input
                                type="radio"
                                name="apps-deploy-arch"
                                value={arch}
                                checked={isActive}
                                onChange={() => setPickedArchitecture(arch)}
                                disabled={deploying}
                                className="apps-modal-arch-option-radio"
                              />
                              <span className="apps-modal-arch-option-title">
                                {label}
                                {isRecommended && (
                                  <span className="apps-modal-arch-option-recommended">
                                    Recommended
                                  </span>
                                )}
                              </span>
                              <div className="apps-modal-arch-option-sub">{sub}</div>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })()}

                  <div className="mkt-modal-name-label-row">
                    <label className="mkt-modal-name-label" htmlFor="apps-deploy-name-input">
                      Instance name
                    </label>
                    <p
                      className={`mkt-modal-name-help ${nameTaken ? '' : 'is-valid'}`}
                      style={nameTaken ? { color: '#ef4444' } : undefined}
                      aria-live="polite"
                    >
                      {nameTaken
                        ? 'Name taken — pick another'
                        : (pickedProjectName ? `Available — will deploy to ${pickedProjectName}` : '')}
                    </p>
                  </div>
                  {/* Compact single-row footer — mirrors the workflow
                      MarketplaceWorkflowDeployFooter shape exactly:
                      [name input flex:1] [provider select fixed-width]
                      [Deploy button]. No cred-affordance subtext (suffix
                      confirmation moved into the post-click confirm
                      modal) and no Connect-X swap (missing cred opens
                      the paste modal directly on Deploy-click). */}
                  <div className={`mkt-modal-name-row ${nameTaken ? '' : 'is-valid'}`}>
                    <Field
                      id="apps-deploy-name-input"
                      type="text"
                      className="mkt-modal-name-input"
                      value={instanceName}
                      onChange={(e) => setInstanceName(e.target.value.slice(0, 80))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !deploying) {
                          e.preventDefault();
                          handleDeployClick();
                        }
                      }}
                      placeholder={active.displayName}
                      autoComplete="off"
                      spellCheck="false"
                      disabled={deploying}
                      status={nameTaken ? 'error' : 'ok'}
                    />
                    {/* Native <select> styled by .mkt-field--select — the
                        SAME control the workflow footer uses. Two
                        options only (Claude default, Codex). No
                        description sub-lines: vendor + model is all the
                        user needs at the deploy step. */}
                    <select
                      className="mkt-field mkt-field--select"
                      value={pickedProvider}
                      onChange={(e) => setPickedProvider(e.target.value)}
                      disabled={deploying}
                      aria-label="Agent provider for this app"
                      title={pickedProvider === 'codex' ? 'Codex · GPT-5' : 'Claude · Sonnet 4.6'}
                    >
                      <option value="claude">Claude · Sonnet 4.6</option>
                      <option value="codex">Codex · GPT-5</option>
                    </select>
                    {(() => {
                      const tierInfo = resolveTier(active.pricing?.tier);
                      const fiveMinCost = tierInfo.milliCentsPerMin * 5;
                      const insufficient = creditBalanceMc !== null
                        && creditBalanceMc < fiveMinCost;
                      return (
                        <span className="mkt-modal-deploy-split">
                          <button
                            type="button"
                            className="mkt-modal-deploy"
                            disabled={deploying || nameTaken}
                            onClick={handleDeployClick}
                            title={nameTaken ? 'Name taken — pick another' : (insufficient ? 'Add credits to deploy' : undefined)}
                          >
                            <IonIcon icon={rocketOutline} aria-hidden="true" />
                            {deploying ? 'Deploying…' : 'Deploy'}
                          </button>
                        </span>
                      );
                    })()}
                  </div>
                </>
              )}
            </footer>
          </div>
        </div>,
        document.body
      )}

      <ClaudeCredentialModal
        isOpen={claudeModalOpen}
        onClose={() => {
          setClaudeModalOpen(false);
          // User closed the paste modal without saving — abandon the
          // queued confirm step so a later Deploy-click doesn't surprise.
          setPendingConfirmAfterCred(false);
        }}
        defaultKind={claudeCred?.mode || 'oauth'}
        onConnected={async ({ mode, suffix }) => {
          // Cred is saved server-side. Optimistically flip state to A so
          // the next Deploy click bypasses the cred gate, then re-fetch
          // for the canonical suffix.
          setClaudeCred({ hasClaude: true, mode, suffix });
          refreshClaudeCred();
          // If this paste was triggered by a Deploy-click that found no
          // cred, jump straight into the confirm modal with the suffix
          // we just saved. No double-click required.
          if (pendingConfirmAfterCred) {
            setPendingConfirmAfterCred(false);
            setShowConfirmDeploy(true);
          }
        }}
      />

      <OpenAICredentialModal
        isOpen={openaiModalOpen}
        onClose={() => {
          setOpenaiModalOpen(false);
          setPendingConfirmAfterCred(false);
        }}
        onConnected={async ({ suffix }) => {
          setOpenaiCred({ hasOpenAI: true, mode: 'api', suffix });
          refreshOpenaiCred();
          if (pendingConfirmAfterCred) {
            setPendingConfirmAfterCred(false);
            setShowConfirmDeploy(true);
          }
        }}
      />

      <MktAlertModal
        open={showInsufficientCredits}
        onClose={() => setShowInsufficientCredits(false)}
        title="Add credits to deploy"
        ariaLabel="Insufficient credits"
        primaryLabel="Go to Billing"
        primaryTo="/billing"
        onPrimary={() => closeDeploy()}
        secondaryLabel="Cancel"
      >
        {active && (() => {
          const tierInfo = resolveTier(active.pricing?.tier);
          const fiveMinCost = tierInfo.milliCentsPerMin * 5;
          const balanceStr = creditBalanceMc !== null
            ? formatBalance(creditBalanceMc, { precision: 'auto' })
            : '—';
          const minStr = formatBalance(fiveMinCost, { precision: 'auto' });
          return (
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.55, marginBottom: 20 }}>
              Deploying requires at least <strong style={{ color: '#fff' }}>5 minutes</strong> of
              runtime at this app&apos;s rate ({tierInfo.hourly}).
              Your balance is <strong style={{ color: '#fff' }}>{balanceStr}</strong>
              {' '}(minimum <strong style={{ color: '#fff' }}>{minStr}</strong>).
              {' '}Add credits on the{' '}
              <MktAlertModalLink
                to="/billing"
                onClick={() => {
                  setShowInsufficientCredits(false);
                  closeDeploy();
                }}
              >
                billing page
              </MktAlertModalLink>
              {' '}to continue.
            </p>
          );
        })()}
      </MktAlertModal>

      {/* Confirm-deploy modal — pops after the user clicks the row's
          Deploy button and a credential exists for the picked provider.
          Shows the instance + project + credential suffix the deploy
          will use. Primary fires the actual POST via performDeploy. */}
      <MktAlertModal
        open={showConfirmDeploy}
        onClose={() => setShowConfirmDeploy(false)}
        title="Confirm deploy"
        ariaLabel="Confirm deploy"
        primaryLabel={deploying ? 'Deploying…' : 'Deploy'}
        onPrimary={() => { performDeploy(); }}
        secondaryLabel="Cancel"
      >
        {active && (() => {
          const isCodex = pickedProvider === 'codex';
          const cred = isCodex ? openaiCred : claudeCred;
          const instanceLabel = (instanceName.trim() || active.displayName || '').trim();
          const projectLabel = pickedProjectName || 'this project';
          const suffix = cred?.suffix ? `···${cred.suffix}` : null;
          return (
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
              <p style={{ margin: '0 0 12px 0' }}>
                Deploy <strong style={{ color: '#fff' }}>{instanceLabel}</strong>
                {' '}to <strong style={{ color: '#fff' }}>{projectLabel}</strong>?
              </p>
              {suffix && (
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>
                  {isCodex ? (
                    <>Will use <strong style={{ color: '#fff' }}>your OpenAI API key</strong> ({suffix}).</>
                  ) : (
                    <>Will use <strong style={{ color: '#fff' }}>your Claude{' '}
                      {cred?.mode === 'oauth' ? 'subscription' : 'API key'}</strong> ({suffix}).</>
                  )}
                </p>
              )}
            </div>
          );
        })()}
      </MktAlertModal>
    </>
  );
};

export default AppsMarketplaceSection;
